import { useId, type FormEventHandler, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Modal,
  Spinner,
  type ModalSize,
} from '@skupreme/shared/ui';

/** The marketplace row the catalog settings hand to a connect popup. */
export type ConnectMarketplaceItem = {
  marketplace_id?: string | string[];
  marketplace_region_id?: string;
  alias?: string;
  name?: string;
};

type ConnectPopupProps = {
  open: boolean;
  title: ReactNode;
  /** Cancel, the X, Esc and the backdrop all land here. */
  onClose: () => void;
  children?: ReactNode;
  /** Wraps the body in a form submitted by the footer's primary button. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  submitLabel?: string;
  isSubmitting?: boolean;
  error?: string;
  success?: string;
  /** False leaves no way out but the submit button. */
  closeable?: boolean;
  /** Replaces the default Cancel and submit pair. */
  footer?: ReactNode;
  size?: ModalSize;
  className?: string;
};

/**
 * The frame every marketplace connect popup shares: a titled kit Modal, the
 * form, its error or success notice, and Cancel with the action in the footer.
 */
export function ConnectPopup({
  open,
  title,
  onClose,
  children,
  onSubmit,
  submitLabel = 'Connect',
  isSubmitting = false,
  error,
  success,
  closeable = true,
  footer,
  size = 'md',
  className,
}: ConnectPopupProps) {
  const formId = useId();

  const notices = (
    <>
      {success ? <Alert tone="success">{success}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
    </>
  );

  const actions = footer ?? (
    <>
      {closeable ? <Button onPress={onClose}>Cancel</Button> : null}
      {onSubmit ? (
        <Button
          type="submit"
          form={formId}
          variant="primary"
          isPending={isSubmitting}
        >
          {isSubmitting ? <Spinner size="sm" label={submitLabel} /> : null}
          {submitLabel}
        </Button>
      ) : null}
    </>
  );

  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
      title={title}
      size={size}
      showCloseButton={closeable}
      isDismissable={closeable}
      footer={actions}
      className={className}
    >
      {onSubmit ? (
        <form
          id={formId}
          noValidate
          autoComplete="off"
          onSubmit={onSubmit}
          className="sk:flex sk:flex-col sk:gap-4"
        >
          {children}
          {notices}
        </form>
      ) : (
        <div className="sk:flex sk:flex-col sk:gap-4">
          {children}
          {notices}
        </div>
      )}
    </Modal>
  );
}

/** A marketplace logo over the one line telling the user what to enter. */
export function ConnectIntro({
  logo,
  children,
}: {
  logo?: string;
  children: ReactNode;
}) {
  return (
    <div className="sk:flex sk:flex-col sk:items-center sk:gap-3 sk:text-center">
      {logo ? (
        <img src={logo} alt="" className="sk:h-10 sk:w-auto sk:max-w-40" />
      ) : null}
      <p className="sk:m-0 sk:text-sm sk:font-medium sk:text-fg">{children}</p>
    </div>
  );
}

/** Small print under the fields: where the credentials come from. */
export function ConnectNote({ children }: { children: ReactNode }) {
  return <p className="sk:m-0 sk:text-xs sk:text-fg-tertiary">{children}</p>;
}

/**
 * The API rejects with `{ error }` and thrown errors carry `message`; each
 * popup reads the one it always has.
 */
export function failureText(
  failure: unknown,
  key: 'error' | 'message',
  fallback: string,
): string {
  const text =
    typeof failure === 'object' && failure !== null && key in failure
      ? failure[key]
      : undefined;
  return typeof text === 'string' && text ? text : fallback;
}
